import React, { useState, useRef, useEffect, useMemo } from "react"
import api from "../../api/axiosInstance"
import _ from "lodash"

import { useNavigate } from "react-router-dom"
import { DateTime } from "luxon"

// hooks
import useSubscribeAllDocs from "../../hooks/useSubscribeAllDocs"

import { DELETE } from "../../config/constants"

// PR
import { Button } from "primereact/button"
import { SplitButton } from "primereact/splitbutton"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Toast } from "primereact/toast"

import Breadcrumb from "../../components/Breadcrumb"

export default function UserList() {
  const [deleting, setDeleting] = useState(false)
  const [pages, setPages] = useState([])
  const [currentPageIndex, setCurrentPageIndex] = useState(0)

  const hookParams = {
    collection: "users",
    withRef: {
      key: "ref_organization",
      value: ["organization_name"],
    },
    limit: 15,
    startAfterDoc: pages[currentPageIndex - 1] || null,
  }

  const [loading, error, data, lastVisible] = useSubscribeAllDocs(hookParams)

  useEffect(() => {
    if (lastVisible && pages[currentPageIndex] !== lastVisible) {
      const newPages = [...pages]
      newPages[currentPageIndex] = lastVisible
      setPages(newPages)
    }
  }, [lastVisible])

  const navigate = useNavigate()
  const toast = useRef(null)

  const handleDelete = async (email) => {
    setDeleting(true)
    try {
      const response = await api.post(`/${DELETE.USER_PROFILE}`, { email })
      if (response.data.status === "success") {
        toast.current.show({
          severity: "success",
          summary: "User Profile Deleted",
          detail: "The user was successfully deleted.",
          life: 3000,
        })
        setDeleting(false)
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error Deleting User",
        detail: "An error occurred while deleting the user.",
        life: 3000,
      })
      setDeleting(false)
    }
  }

  const handleData = useMemo(() => {
    if (!data) return []
    return _.map(data, (row) => {
      const handleActions = (row) => {
        const { id, email } = row
        const items = [
          {
            label: "Delete",
            icon: "pi pi-times",
            command: () => handleDelete(email),
          },
        ]
        return (
          <SplitButton
            disabled={deleting}
            label="Edit"
            onClick={() => navigate(`/user/edit/${id}`)}
            model={items}
          />
        )
      }

      const handleRoleName = () => {
        switch (row.user_role) {
          case 0:
            return "SysAdmin"
          case 1:
            return "THS Admin"
          case 10:
            return "Analyst"
          case 11:
            return "Authorized Admin"
          case 15:
            return "Authorized User"
          default:
            return "User"
        }
      }

      const modDate = row.timestamp_updated
        ? row.timestamp_updated.toDate()
        : null

      return {
        ...row,
        updated: DateTime.fromJSDate(modDate).toFormat("F"),
        organization_name: row.ref_organization.organization_name,
        user_role: handleRoleName(),
        actions: handleActions(row),
      }
    })
  }, [data, deleting])

  if (error) {
    return (
      <div>
        <h2>Error when fetching.</h2>
      </div>
    )
  }

  const itemsAddUser = [
    {
      label: "HFMA User",
      command: () => navigate("/user/add-hfma"),
    },
  ]

  return (
    <div className="container">
      <h1>Users</h1>
      <Breadcrumb items={[{ label: "User Directory" }]} />

      <SplitButton
        label="Add New User"
        icon="pi pi-plus"
        onClick={() => navigate("/user/add")}
        model={itemsAddUser}
        style={{ marginBottom: 15 }}
      />

      <DataTable value={handleData} responsiveLayout="scroll" loading={loading}>
        <Column field="name_first" header="First Name" filter sortable />
        <Column field="name_last" header="Last Name" sortable />
        <Column field="email" header="Email" sortable />
        <Column field="user_role" header="Role" sortable />
        <Column field="organization_name" header="Organization" sortable />
        <Column field="updated" header="Last Update" />
        <Column field="actions" header="Actions" />
      </DataTable>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "1rem",
          marginTop: 16,
        }}
      >
        <Button
          label="Previous"
          icon="pi pi-angle-left"
          onClick={() => setCurrentPageIndex((i) => Math.max(i - 1, 0))}
          disabled={currentPageIndex === 0}
        />
        <Button
          label="Next"
          icon="pi pi-angle-right"
          iconPos="right"
          onClick={() => setCurrentPageIndex((i) => i + 1)}
          disabled={!lastVisible || !data || data.length < hookParams.limit}
        />
      </div>

      <Toast ref={toast} />
    </div>
  )
}
